import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  CButton,
  CCollapse,
  CContainer,
  CNav,
  CNavItem,
  CNavbar,
  CNavbarToggler,
} from "@coreui/react-pro";
import {
  isPublicNavItemActive,
  publicActionNavItems,
  publicAuthNavItems,
  publicMarketingNavItems,
} from "../../config/publicNavigation";

const brandLogoSrc = `${process.env.PUBLIC_URL}/assets/brand/experts4europe-logo.png`;
const madeInEuFlagSrc = `${process.env.PUBLIC_URL}/assets/brand/made_in_eu.png`;
const madeInEuHref = "https://european-union.europa.eu/index_de";

function PublicNavLink({ item, className, onNavigate, pathname }) {
  const activeClassName = isPublicNavItemActive(item, pathname) ? " is-active" : "";
  const resolvedClassName = `${className}${activeClassName}`.trim();
  const isHashLink = item.href.startsWith("#");

  if (isHashLink) {
    return (
      <a href={pathname === "/" ? item.href : `/${item.href}`} className={resolvedClassName} onClick={onNavigate}>
        {item.label}
      </a>
    );
  }

  return (
    <Link to={item.href} className={resolvedClassName} onClick={onNavigate}>
      {item.label}
    </Link>
  );
}

function MadeInEuBadge() {
  return (
    <a
      href={madeInEuHref}
      target="_blank"
      rel="noreferrer"
      className="public-core-made-in-eu"
      aria-label="Made in EU - offizielles EU-Emblem"
    >
      <img src={madeInEuFlagSrc} alt="" className="public-core-made-in-eu__flag" />
    </a>
  );
}

function resolveActionClassName({ action, pathname, activeAuthPath, mode }) {
  if (action.key === "quickcheck") {
    return `public-core-btn ${
      pathname === "/applicant/frontdoor" ? "public-core-btn--subtle is-current" : "public-core-btn--ghost"
    }`.trim();
  }

  if (action.key === "login") {
    const isActive = mode === "auth" && activeAuthPath === "/login";
    return `public-core-btn ${isActive ? "public-core-btn--subtle is-current" : "public-core-btn--ghost"}`.trim();
  }

  if (action.key === "register") {
    const isActive = mode === "auth" && activeAuthPath === "/register";
    return `public-core-btn ${isActive ? "public-core-btn--primary is-current" : "public-core-btn--primary"}`.trim();
  }

  return "public-core-btn public-core-btn--ghost";
}

function renderActionButtons({ navigate, pathname, activeAuthPath, mode, onAfterClick = null }) {
  return publicActionNavItems.map((action) => (
    <CButton
      key={action.key}
      type="button"
      className={resolveActionClassName({ action, pathname, activeAuthPath, mode })}
      onClick={() => {
        navigate(action.href);
        if (typeof onAfterClick === "function") {
          onAfterClick();
        }
      }}
    >
      {action.label}
    </CButton>
  ));
}

export default function PublicHeader({ mode = "marketing", activeAuthPath = null, tone = "light" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const navItems = mode === "auth" ? publicAuthNavItems : publicMarketingNavItems;
  const [mobileVisible, setMobileVisible] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const brandMeta =
    mode === "auth"
      ? "Zugang, Registrierung und strukturierte nächste Schritte"
      : "Faire berufliche Wege und verantwortungsvolle Rekrutierung in Europa";

  useEffect(() => {
    setMobileVisible(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const updateScrollState = () => {
      setIsScrolled(window.scrollY > 18);
    };

    updateScrollState();
    window.addEventListener("scroll", updateScrollState, { passive: true });

    return () => window.removeEventListener("scroll", updateScrollState);
  }, []);

  return (
    <CNavbar
      expand="lg"
      className={`public-core-navbar public-core-navbar--${mode} public-core-navbar--tone-${tone} ${
        isScrolled ? "is-scrolled" : ""
      }`.trim()}
    >
      <CContainer fluid className="public-core-container">
        <div className="public-core-navbar__shell">
          <div className="public-core-navbar__top">
            <Link to="/" className="public-core-brand" aria-label="Experts4Europe Startseite">
              <span className="public-core-brand__logo-wrap" aria-hidden="true">
                <img src={brandLogoSrc} alt="" className="public-core-brand__logo" />
              </span>
              <span className="public-core-brand__copy">
                <small>{brandMeta}</small>
              </span>
            </Link>

            <div className="public-core-navbar__desktop">
              <CNav className="public-core-nav public-core-nav--desktop" component="nav">
                {navItems.map((item) => (
                  <CNavItem key={item.label}>
                    <PublicNavLink item={item} className="public-core-nav__link" pathname={location.pathname} />
                  </CNavItem>
                ))}
              </CNav>

              <div className="public-core-navbar__actions public-core-navbar__actions--desktop">
                <>
                  {renderActionButtons({
                    navigate,
                    pathname: location.pathname,
                    activeAuthPath,
                    mode,
                  })}
                  <MadeInEuBadge />
                </>
              </div>
            </div>

            <CNavbarToggler
              className="public-core-navbar__toggler"
              aria-label="Navigation ein- oder ausblenden"
              aria-expanded={mobileVisible}
              onClick={() => setMobileVisible((current) => !current)}
            />
          </div>

          <CCollapse visible={mobileVisible} className="public-core-navbar__collapse">
            <CNav className="public-core-nav public-core-nav--mobile" component="nav">
              {navItems.map((item) => (
                <CNavItem key={item.label}>
                  <PublicNavLink
                    item={item}
                    className="public-core-nav__link public-core-nav__link--mobile"
                    pathname={location.pathname}
                    onNavigate={() => setMobileVisible(false)}
                  />
                </CNavItem>
              ))}
            </CNav>

            <div className="public-core-navbar__actions public-core-navbar__actions--mobile">
              <>
                {renderActionButtons({
                  navigate,
                  pathname: location.pathname,
                  activeAuthPath,
                  mode,
                  onAfterClick: () => setMobileVisible(false),
                })}
                <MadeInEuBadge />
              </>
            </div>
          </CCollapse>
        </div>
      </CContainer>
    </CNavbar>
  );
}
